'use client';

import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Alert, Box, Typography } from '@mui/material';
import { useMarketingSectionSpy } from '@/components/website/Marketing/marketing-scroll';
import { MARKETING } from '@/components/website/Marketing/marketing-tokens';
import WebsiteInteriorScrollLayout from '@/components/website/WebsiteInteriorScrollLayout';
import MarketingPageSections from './MarketingPageSections';
import { BRAND_CMS_SLUG, indexSpyIdsFromSections } from './marketing-page-utils';

export default function MarketingPageView({
  fontClassName,
  page: initialPage = null,
  preview = false,
  slug = BRAND_CMS_SLUG,
}) {
  const [page, setPage] = useState(initialPage);
  const [previewError, setPreviewError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(preview);

  useEffect(() => {
    if (!preview) return undefined;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError('');
    axios
      .get(`/api/v2/gallery/marketing-pages/${encodeURIComponent(slug)}/preview/`, {
        withCredentials: true,
      })
      .then(({ data }) => {
        if (!cancelled) {
          setPage(data);
          setPreviewLoading(false);
        }
      })
      .catch(error => {
        if (cancelled) return;
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          setPreviewError('Draft preview requires a signed-in CMS editor.');
        } else if (status === 404) {
          setPreviewError('Draft preview is not available for this page.');
        } else {
          setPreviewError('Unable to load draft preview.');
        }
        setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [preview, slug]);

  const sections = page?.sections ?? [];
  const spyIds = useMemo(() => indexSpyIdsFromSections(sections), [sections]);
  const activeId = useMarketingSectionSpy(spyIds);

  return (
    <WebsiteInteriorScrollLayout
      contentSx={{
        bgcolor: MARKETING.canvas,
        color: MARKETING.text,
        p: 0,
      }}
    >
      <Box
        className={fontClassName}
        sx={{
          bgcolor: MARKETING.canvas,
          color: MARKETING.text,
          overflowX: 'clip',
        }}
      >
        {preview ? (
          <Alert
            severity="warning"
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 20,
              borderRadius: 0,
            }}
          >
            Draft preview — unpublished changes. Not visible to the public.
          </Alert>
        ) : null}
        {previewLoading ? (
          <Typography sx={{ px: MARKETING.padX, py: 8 }} color="text.secondary">
            Loading draft preview…
          </Typography>
        ) : null}
        {previewError ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {previewError}
          </Alert>
        ) : null}
        {!previewLoading && page ? (
          <MarketingPageSections sections={sections} activeId={activeId} />
        ) : null}
        {!previewLoading && !page && !previewError ? (
          <Typography sx={{ px: MARKETING.padX, py: 8 }}>Page not found.</Typography>
        ) : null}
      </Box>
    </WebsiteInteriorScrollLayout>
  );
}

MarketingPageView.propTypes = {
  fontClassName: PropTypes.string,
  page: PropTypes.object,
  preview: PropTypes.bool,
  slug: PropTypes.string,
};
